import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {useWalkMap} from './useWalkMap';
import {DrinkingFountainIcon, FullscreenIcon, LocationOnIcon} from 'Images/icons';

import type {FitPadding, FountainMarker, MapMarker, MapVariant} from './types';

/** Above the app's fixed bottom navigation bar (z-index 50) and all in-map overlays, so fullscreen truly covers the viewport. */
const FULLSCREEN_Z_INDEX = 1000;

/**
 * The props for the `WalkMap` component.
 * Configures the interactive Leaflet map with waypoint markers, hover tooltips,
 * and a click-to-open info card overlay.
 */
interface ComponentProps {
    /**
     * Optional overlay content rendered on top of the map (e.g. A category filter bar or a walk
     * slider), positioned by the caller. Lives inside the same stacking context as the map, so it
     * stays visible and correctly positioned when fullscreen mode is toggled.
     */
    children?: ReactNode;
    /**
     * Optional per-side auto-fit padding override, so callers with overlays (e.g. A filter bar,
     * a walk slider) can reserve space around the markers.
     */
    fitPadding?: FitPadding;
    /**
     * Optional drinking-fountain points. Shown as their own toggleable layer (hidden by default,
     * with a toggle button rendered below the locate button), independent of `selectedWalkIds`.
     */
    fountains?: FountainMarker[];
    /** Height of the map container. Accepts any CSS value. */
    height?: string;
    /** Array of markers to render on the map. */
    markers: MapMarker[];
    /**
     * Called whenever the clicked marker changes. Used by `full` variant callers to surface
     * marker details in their own UI instead of the (suppressed) floating info card.
     */
    onActiveMarkerChange?(marker: MapMarker | null): void;
    /** Optional walk IDs to filter markers by, used to show only currently selected walks in `full` variant. */
    selectedWalkIds?: string[] | null;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'WalkMap'
     */
    testId?: string;
    /**
     * Display variant:
     * - `preview`: Public detail page — shows title only in info card.
     * - `protected`: Purchased detail page — shows title + description.
     * - `full`: Overview page — no floating info card; use `onActiveMarkerChange` instead.
     */
    variant?: MapVariant;
}

/**
 * Renders an interactive Leaflet map with waypoint markers.
 * Supports three display variants for different page contexts:
 * - `preview` for the public walk detail page with minimal info.
 * - `protected` for the purchased walk detail page with descriptions.
 * - `full` for the overview page — no floating info card is shown; the active marker is
 * reported via `onActiveMarkerChange` so the caller can surface it in its own UI.
 *
 * Markers show a tooltip on hover and set the active marker on click.
 * The map auto-fits bounds to show all visible markers.
 *
 * @param props                      The component props.
 * @param props.children             Optional overlay content (e.g. Filter bar, walk slider) rendered on top of the map.
 * @param props.fitPadding           Optional per-side auto-fit padding override.
 * @param props.fountains            Optional drinking-fountain points, shown via a toggleable layer (hidden by default).
 * @param props.height               Optional map container height. Defaults to `'40rem'`.
 * @param props.markers              Array of map markers with coordinates and metadata.
 * @param props.onActiveMarkerChange Called whenever the active marker changes.
 * @param props.selectedWalkIds      Optional walk IDs to filter markers by in `full` mode.
 * @param props.testId               An optional test ID used for automation selectors. Defaults to `'WalkMap'`.
 * @param props.variant              Display variant controlling info card detail level. Defaults to `'preview'`.
 * @returns A JSX element rendering the WalkMap.
 *
 * @example
 * ```tsx
 * <WalkMap markers={waypointMarkers} variant="preview" />
 * ```
 */
const WalkMap = ({
    children,
    fitPadding,
    fountains,
    height = '40rem',
    markers,
    onActiveMarkerChange,
    selectedWalkIds,
    testId = 'WalkMap',
    variant = 'preview'
}: ComponentProps) => {
    const {
        activeMarker,
        containerRef,
        handleCloseCard,
        handleFullscreenHintClick,
        handleLocateMe,
        isFullscreen,
        isReady,
        showFountains,
        showGestureHint,
        t,
        toggleFountains,
        toggleFullscreen
    } = useWalkMap(markers, selectedWalkIds, onActiveMarkerChange, fitPadding, fountains);

    return (
        <Wrapper $height={height} $isFullscreen={isFullscreen} data-cy={testId}>
            <MapContainer ref={containerRef} data-cy={`${testId}-map`} />
            {!isReady && <LoadingOverlay>{t('walkMap.loading')}</LoadingOverlay>}
            {showGestureHint && (
                <GestureHintOverlay data-cy={`${testId}-gesture-hint`}>
                    <GestureHintText>{t('walkMap.twoFingerHint')}</GestureHintText>
                    <GestureHintButton
                        data-cy={`${testId}-gesture-hint-fullscreen`}
                        type="button"
                        onClick={handleFullscreenHintClick}
                    >
                        {t('walkMap.fullscreenHintButton')}
                    </GestureHintButton>
                </GestureHintOverlay>
            )}
            <FullscreenButton
                aria-label={isFullscreen ? t('walkMap.fullscreenExit') : t('walkMap.fullscreenEnter')}
                data-cy={`${testId}-fullscreen`}
                type="button"
                onClick={toggleFullscreen}
            >
                {isFullscreen ? '✕' : <FullscreenIcon height={18} width={18} />}
            </FullscreenButton>
            <LocateButton
                aria-label={t('walkMap.locateMe')}
                data-cy={`${testId}-locate`}
                type="button"
                onClick={handleLocateMe}
            >
                <LocationOnIcon height={18} width={18} />
            </LocateButton>
            {fountains && fountains.length > 0 && (
                <FountainToggleButton
                    $active={showFountains}
                    aria-label={showFountains ? t('walkMap.fountainsHide') : t('walkMap.fountainsShow')}
                    aria-pressed={showFountains}
                    data-cy={`${testId}-fountains-toggle`}
                    type="button"
                    onClick={toggleFountains}
                >
                    <DrinkingFountainIcon height={18} width={18} />
                </FountainToggleButton>
            )}
            {activeMarker && variant !== 'full' && (
                <InfoCard data-cy={`${testId}-infocard`}>
                    <CloseButton
                        $hasImage={Boolean(activeMarker.imageUrl)}
                        aria-label={t('walkMap.close')}
                        data-cy={`${testId}-close`}
                        type="button"
                        onClick={handleCloseCard}
                    >
                        ✕
                    </CloseButton>
                    {activeMarker.imageUrl && (
                        <InfoImageWrapper>
                            <ResponsiveImage
                                alt={activeMarker.imageAlt ?? activeMarker.title}
                                sizes="26rem"
                                src={activeMarker.imageUrl}
                            />
                        </InfoImageWrapper>
                    )}
                    <InfoContent>
                        <InfoTitle>{activeMarker.title}</InfoTitle>
                        {activeMarker.description && (
                            <InfoDescription>{activeMarker.description}</InfoDescription>
                        )}
                        {activeMarker.link && (
                            <InfoLink href={activeMarker.link}>
                                {activeMarker.linkLabel ?? t('walkMap.detailsLink')}
                            </InfoLink>
                        )}
                    </InfoContent>
                </InfoCard>
            )}
            {children}
        </Wrapper>
    );
};

WalkMap.displayName = 'WalkMap';

export {WalkMap};

// ── Styled ──────────────────────────────────────────────────

interface WrapperProps {
    /** The height of the map wrapper. */
    $height: string;
    /** Whether the map is currently shown in fullscreen mode. */
    $isFullscreen: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    border-radius: ${({$isFullscreen}) => ($isFullscreen ? '0' : spacing(4))};
    height: ${({$height, $isFullscreen}) => ($isFullscreen ? '100dvh' : $height)};
    inset: ${({$isFullscreen}) => ($isFullscreen ? '0' : 'auto')};
    isolation: isolate;
    overflow: hidden;
    position: ${({$isFullscreen}) => ($isFullscreen ? 'fixed' : 'relative')};
    width: ${({$isFullscreen}) => ($isFullscreen ? '100vw' : 'auto')};
    z-index: ${({$isFullscreen}) => ($isFullscreen ? FULLSCREEN_Z_INDEX : 'auto')};
`;

const MapContainer = styled.div`
    height: 100%;
    width: 100%;
`;

const LoadingOverlay = styled.div`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceMid};
    color: ${({theme}) => theme.colors.inkTertiary};
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    inset: 0;
    justify-content: center;
    position: absolute;
`;

const GestureHintOverlay = styled.div`
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    color: ${({theme}) => theme.colors.white};
    display: flex;
    flex-direction: column;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    gap: ${spacing(4)};
    inset: 0;
    justify-content: center;
    padding: ${spacing(8)};
    pointer-events: none;
    position: absolute;
    text-align: center;
    z-index: 1000;
`;

const GestureHintText = styled.p`
    font-size: 1.6rem;
    font-weight: 600;
    margin: 0;
`;

const GestureHintButton = styled.button`
    background: ${({theme}) => theme.colors.white};
    border: none;
    border-radius: ${spacing(2)};
    color: ${({theme}) => theme.colors.inkPrimary};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 700;
    padding: ${spacing(3)} ${spacing(6)};
    pointer-events: auto;

    &:active {
        background: ${({theme}) => theme.colors.surfaceMid};
    }
`;

const FullscreenButton = styled.button`
    align-items: center;
    backdrop-filter: blur(8px);
    background: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 50%;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    display: flex;
    font-size: 1.4rem;
    height: ${spacing(9)};
    justify-content: center;
    position: absolute;
    right: ${spacing(4)};
    top: ${spacing(4)};
    width: ${spacing(9)};
    z-index: 900;

    &:active {
        background: rgba(0, 0, 0, 0.6);
    }
`;

/* Stacked directly below the fullscreen button — the only corner free on every variant (bottom-left/right can hold an info card or, in `full` mode, a walk slider). */
const LocateButton = styled(FullscreenButton)`
    top: calc(${spacing(4)} + ${spacing(9)} + ${spacing(2)});
`;

interface FountainToggleButtonProps {
    /** Whether the drinking-fountain layer is currently shown. */
    $active: boolean;
}

/* Stacked directly below the locate button. */
const FountainToggleButton = styled(FullscreenButton)<FountainToggleButtonProps>`
    background: ${({$active, theme}) => ($active ? theme.colors.primaryDefault : 'rgba(0, 0, 0, 0.4)')};
    top: calc(${spacing(4)} + ${spacing(9)} + ${spacing(2)} + ${spacing(9)} + ${spacing(2)});
`;

/*
 * Anchored bottom-left (not spanning the full map width) — a compact tile rather than a
 * full-width banner. \`width\` caps at \`26rem\` but shrinks on narrow screens via \`calc\`.
 */
const InfoCard = styled.div`
    background: ${({theme}) => theme.colors.white};
    border-radius: ${spacing(4)};
    bottom: ${spacing(4)};
    box-shadow: ${({theme}) => theme.boxShadows.floatingShadow};
    left: ${spacing(4)};
    overflow: hidden;
    position: absolute;
    width: min(26rem, calc(100% - ${spacing(8)}));
    z-index: 1000;
`;

interface CloseButtonProps {
    /** Whether the close button overlays the info card's image (needs a darker, blurred backdrop for legibility). */
    $hasImage: boolean;
}

const CloseButton = styled.button<CloseButtonProps>`
    align-items: center;
    backdrop-filter: ${({$hasImage}) => ($hasImage ? 'blur(8px)' : 'none')};
    background: ${({$hasImage, theme}) => ($hasImage ? 'rgba(0, 0, 0, 0.4)' : theme.colors.surfaceHigh)};
    border: none;
    border-radius: 50%;
    color: ${({$hasImage, theme}) => ($hasImage ? theme.colors.white : theme.colors.inkSecondary)};
    cursor: pointer;
    display: flex;
    font-size: 1.2rem;
    height: ${spacing(7)};
    justify-content: center;
    position: absolute;
    right: ${spacing(3)};
    top: ${spacing(3)};
    width: ${spacing(7)};
    z-index: 1;

    &:active {
        background: ${({$hasImage}) => ($hasImage ? 'rgba(0, 0, 0, 0.6)' : undefined)};
    }
`;

const InfoImageWrapper = styled.div`
    height: 12rem;
    overflow: hidden;
    width: 100%;
`;

const InfoContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    padding: ${spacing(4)};
`;

const InfoTitle = styled.h3`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.NotoSerif};
    font-size: 1.6rem;
    font-weight: 700;
    margin: 0;
`;

const InfoDescription = styled.p`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    line-height: 1.5;
    margin: 0;
`;

const InfoLink = styled(Link)`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 600;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;